import configs, {buildApiRequestHeaders} from '../configs';
import {
    formatTyphoonForecastLabel,
    getTyphoonDisplayNumber,
    getTyphoonDisplayTitle
} from '../helpers/helpers-typhoon';
import destination from '../turf/destination';

const EMPTY_FEATURE_COLLECTION = {
    type: 'FeatureCollection',
    features: []
};
const DEGREE_TO_RADIAN = Math.PI / 180;
const RADIAN_TO_DEGREE = 180 / Math.PI;
const MEAN_EARTH_RADIUS_KM = 6371.0088;

function parseTyphoonPayload(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (payload && typeof payload.body === 'string') {
        try {
            const parsedBody = JSON.parse(payload.body);

            return Array.isArray(parsedBody) ? parsedBody : [];
        } catch (error) {
            console.warn('台風APIレスポンス body の JSON 解析に失敗しました', error);
            return [];
        }
    }
    if (payload && Array.isArray(payload.items)) {
        return payload.items;
    }
    return [];
}

function parseTyphoonDate(value) {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
}

function buildTyphoonRequestUrl() {
    const url = new URL(configs.apiUrl.typhoon, window.location.href);

    url.searchParams.set('_', String(Date.now()));
    return url.toString();
}

function toFiniteNumber(value) {
    const number = Number(value);

    return Number.isFinite(number) ? number : null;
}

function toCoordinateNumber(value) {
    const number = toFiniteNumber(value);

    if (number === null) {
        return null;
    }
    if (Math.abs(number) > 180) {
        return number / 100;
    }
    return number;
}

function toPositiveNumber(value) {
    const number = toFiniteNumber(value);

    return number !== null && number > 0 ? number : 0;
}

function toEventId(value, fallback = '') {
    return String(value || fallback || '').trim();
}

function toBoolean(value) {
    if (typeof value === 'boolean') {
        return value;
    }
    return String(value).toLowerCase() === 'true';
}

function getRecordValue(record, keys) {
    for (const key of keys) {
        if (record && record[key] !== undefined && record[key] !== null && record[key] !== '') {
            return record[key];
        }
    }
    return '';
}

function getCoordinateFromArray(value) {
    if (!Array.isArray(value) || value.length < 2) {
        return null;
    }

    const longitude = toCoordinateNumber(value[0]),
        latitude = toCoordinateNumber(value[1]);

    if (latitude === null || longitude === null) {
        return null;
    }
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
        return null;
    }
    return [longitude, latitude];
}

function getPointCoordinate(record, preferProbabilityCircle = false) {
    const coord = getCoordinateFromArray(record.coordinate || record.coord || record.center);

    if (coord) {
        return coord;
    }

    const latitudeKeys = preferProbabilityCircle ? [
            'probability_circle_latitude',
            'probabilityCircleLatitude',
            'latitude',
            'center_latitude',
            'centerLatitude'
        ] : [
            'center_latitude',
            'centerLatitude',
            'latitude',
            'probability_circle_latitude',
            'probabilityCircleLatitude'
        ],
        longitudeKeys = preferProbabilityCircle ? [
            'probability_circle_longitude',
            'probabilityCircleLongitude',
            'longitude',
            'center_longitude',
            'centerLongitude'
        ] : [
            'center_longitude',
            'centerLongitude',
            'longitude',
            'probability_circle_longitude',
            'probabilityCircleLongitude'
        ],
        latitude = toCoordinateNumber(getRecordValue(record, latitudeKeys)),
        longitude = toCoordinateNumber(getRecordValue(record, longitudeKeys));

    if (latitude === null || longitude === null) {
        return null;
    }
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
        return null;
    }
    return [longitude, latitude];
}

function resolveCircularRadius(record, radiusKeys, narrowRadiusKeys) {
    const primaryRadius = toPositiveNumber(getRecordValue(record, radiusKeys)),
        narrowRadius = toPositiveNumber(getRecordValue(record, narrowRadiusKeys));

    return Math.max(primaryRadius, narrowRadius);
}

function createCircleFeature(center, radiusKm, properties) {
    if (!center || !radiusKm) {
        return null;
    }

    const coordinates = [];

    for (let bearing = 0; bearing <= 360; bearing += 5) {
        coordinates.push(destination(center, radiusKm, bearing));
    }

    return {
        type: 'Feature',
        properties,
        geometry: {
            type: 'Polygon',
            coordinates: [coordinates]
        }
    };
}

function createPointFeature(point) {
    if (!point.coordinate) {
        return null;
    }

    return {
        type: 'Feature',
        properties: {
            kind: 'point',
            pointType: point.isCurrent ? 'current' : 'forecast',
            label: point.label
        },
        geometry: {
            type: 'Point',
            coordinates: point.coordinate
        }
    };
}

function createLabelFeature(point) {
    if (!point.coordinate) {
        return null;
    }

    return {
        type: 'Feature',
        properties: {
            kind: 'label',
            label: point.label
        },
        geometry: {
            type: 'Point',
            coordinates: point.coordinate
        }
    };
}

function createProjectionReference(startCoordinate, endCoordinate) {
    const longitude = (startCoordinate[0] + endCoordinate[0]) / 2,
        latitude = (startCoordinate[1] + endCoordinate[1]) / 2,
        latitudeCos = Math.cos(latitude * DEGREE_TO_RADIAN);

    return {
        longitude,
        latitude,
        latitudeCos: Math.abs(latitudeCos) > 1e-6 ? latitudeCos : 1e-6
    };
}

function projectCoordinate(coordinate, reference) {
    return {
        x: (coordinate[0] - reference.longitude) * DEGREE_TO_RADIAN * MEAN_EARTH_RADIUS_KM * reference.latitudeCos,
        y: (coordinate[1] - reference.latitude) * DEGREE_TO_RADIAN * MEAN_EARTH_RADIUS_KM
    };
}

function unprojectCoordinate(point, reference) {
    return [
        reference.longitude + point.x / (MEAN_EARTH_RADIUS_KM * reference.latitudeCos) * RADIAN_TO_DEGREE,
        reference.latitude + point.y / MEAN_EARTH_RADIUS_KM * RADIAN_TO_DEGREE
    ];
}

function createProbabilityOutlineSegmentFeature(startPoint, endPoint, side, segmentIndex) {
    const radius1 = startPoint.probability_circle_radius_km,
        radius2 = endPoint.probability_circle_radius_km;

    if (!startPoint.coordinate || !endPoint.coordinate || radius1 <= 0 || radius2 <= 0) {
        return null;
    }

    const reference = createProjectionReference(startPoint.coordinate, endPoint.coordinate),
        center1 = projectCoordinate(startPoint.coordinate, reference),
        center2 = projectCoordinate(endPoint.coordinate, reference),
        dx = center2.x - center1.x,
        dy = center2.y - center1.y,
        distance = Math.hypot(dx, dy);

    if (!distance) {
        return null;
    }

    const alpha = (radius1 - radius2) / distance;

    if (Math.abs(alpha) >= 1) {
        return null;
    }

    const ux = dx / distance,
        uy = dy / distance,
        leftNormalX = -uy,
        leftNormalY = ux,
        sideSign = side === 'left' ? 1 : -1,
        beta = sideSign * Math.sqrt(1 - alpha * alpha),
        normalX = alpha * ux + beta * leftNormalX,
        normalY = alpha * uy + beta * leftNormalY,
        tangentPoint1 = {
            x: center1.x + radius1 * normalX,
            y: center1.y + radius1 * normalY
        },
        tangentPoint2 = {
            x: center2.x + radius2 * normalX,
            y: center2.y + radius2 * normalY
        };

    return {
        type: 'Feature',
        properties: {
            kind: 'probability-outline',
            side,
            segmentIndex
        },
        geometry: {
            type: 'LineString',
            coordinates: [
                unprojectCoordinate(tangentPoint1, reference),
                unprojectCoordinate(tangentPoint2, reference)
            ]
        }
    };
}

function createProbabilityOutlineFeatures(points, side) {
    const features = [];

    for (let i = 0; i < points.length - 1; i++) {
        const feature = createProbabilityOutlineSegmentFeature(points[i], points[i + 1], side, i);

        if (feature) {
            features.push(feature);
        }
    }
    return features;
}

function buildTyphoonFeatureCollection(current, forecasts) {
    if (!current) {
        return EMPTY_FEATURE_COLLECTION;
    }

    const points = [current, ...(forecasts || [])].filter(point => point && point.coordinate),
        forecastPointsWithProbabilityCircle = (forecasts || []).filter(point =>
            point &&
            point.coordinate &&
            point.probability_circle_radius_km > 0
        ),
        probabilityOutlineFeatures = [
            ...createProbabilityOutlineFeatures(forecastPointsWithProbabilityCircle, 'left'),
            ...createProbabilityOutlineFeatures(forecastPointsWithProbabilityCircle, 'right')
        ],
        features = [];

    if (points.length > 1) {
        features.push({
            type: 'Feature',
            properties: {
                kind: 'track'
            },
            geometry: {
                type: 'LineString',
                coordinates: points.map(point => point.coordinate)
            }
        });
    }
    features.push(...probabilityOutlineFeatures);

    for (const point of points) {
        const strongWindFeature = createCircleFeature(point.coordinate, point.strong_wind_radius_km, {
                kind: 'wind-area',
                areaType: 'strong-wind'
            }),
            stormAreaFeature = point.isCurrent ?
                createCircleFeature(point.coordinate, point.storm_area_wind_radius_km, {
                    kind: 'wind-area',
                    areaType: 'storm-area'
                }) :
                null,
            stormWarningFeature = !point.isCurrent ?
                createCircleFeature(point.coordinate, point.storm_warning_zone_radius_km, {
                    kind: 'wind-area',
                    areaType: 'storm-warning'
                }) :
                null,
            probabilityCircleFeature = !point.isCurrent ?
                createCircleFeature(point.coordinate, point.probability_circle_radius_km, {
                    kind: 'probability-circle'
                }) :
                null,
            pointFeature = createPointFeature(point),
            labelFeature = createLabelFeature(point);

        if (strongWindFeature) {
            features.push(strongWindFeature);
        }
        if (stormAreaFeature) {
            features.push(stormAreaFeature);
        }
        if (stormWarningFeature) {
            features.push(stormWarningFeature);
        }
        if (probabilityCircleFeature) {
            features.push(probabilityCircleFeature);
        }
        if (pointFeature) {
            features.push(pointFeature);
        }
        if (labelFeature) {
            features.push(labelFeature);
        }
    }

    return {
        type: 'FeatureCollection',
        features
    };
}

function sortTyphoonPoints(a, b) {
    const aDate = parseTyphoonDate(a.forecast_time),
        bDate = parseTyphoonDate(b.forecast_time);

    if (a.isCurrent !== b.isCurrent) {
        return a.isCurrent ? -1 : 1;
    }
    if (aDate && bDate) {
        return aDate - bDate;
    }
    if (aDate) {
        return -1;
    }
    if (bDate) {
        return 1;
    }
    return String(a.forecast_time || '').localeCompare(String(b.forecast_time || ''));
}

function sortTyphoons(a, b) {
    const aDate = parseTyphoonDate(a.report_datetime || a.current?.forecast_time),
        bDate = parseTyphoonDate(b.report_datetime || b.current?.forecast_time);

    if (aDate && bDate) {
        return bDate - aDate;
    }
    if (bDate) {
        return 1;
    }
    if (aDate) {
        return -1;
    }
    return String(b.eventid || '').localeCompare(String(a.eventid || ''));
}

function normalizeTyphoonPoint(record) {
    const isCurrent = String(
            getRecordValue(record, ['live_condition', 'liveCondition', 'isCurrent'])
        ) === '1' || record.isCurrent === true,
        coordinate = getPointCoordinate(record, !isCurrent),
        forecastTime = getRecordValue(record, ['forecast_time', 'forecastTime', 'target_datetime', 'targetDateTime']);

    return {
        forecast_time: forecastTime,
        label: formatTyphoonForecastLabel(forecastTime, isCurrent),
        isCurrent,
        coordinate,
        center_location: getRecordValue(record, ['center_location', 'centerLocation', 'location']),
        center_pressure: getRecordValue(record, ['center_pressure', 'centerPressure', 'pressure']),
        center_speed_kmh: getRecordValue(record, ['center_speed_kmh', 'centerSpeedKmh', 'speedKmh']),
        center_direction: getRecordValue(record, ['center_direction', 'centerDirection', 'direction']),
        area_class: getRecordValue(record, ['area_class', 'areaClass']),
        intensity_class: getRecordValue(record, ['intensity_class', 'intensityClass']),
        max_wind_speed_ms: getRecordValue(record, ['max_wind_speed_ms', 'maxWindSpeedMs']),
        max_instantaneous_wind_speed_ms: getRecordValue(
            record,
            ['max_instantaneous_wind_speed_ms', 'maxInstantaneousWindSpeedMs']
        ),
        probability_circle_radius_km: toPositiveNumber(getRecordValue(
            record,
            ['probability_circle_radius_km', 'probabilityCircleRadiusKm']
        )),
        strong_wind_radius_km: resolveCircularRadius(
            record,
            ['strong_wind_radius_km', 'strongWindRadiusKm'],
            ['strong_wind_narrow_radius_km', 'strongWindNarrowRadiusKm']
        ),
        storm_area_wind_radius_km: resolveCircularRadius(
            record,
            ['storm_area_wind_radius_km', 'stormAreaWindRadiusKm'],
            ['storm_area_wind_narrow_radius_km', 'stormAreaWindNarrowRadiusKm']
        ),
        storm_warning_zone_radius_km: resolveCircularRadius(
            record,
            ['storm_warning_zone_radius_km', 'stormWarningZoneRadiusKm'],
            ['storm_warning_zone_narrow_radius_km', 'stormWarningZoneNarrowRadiusKm']
        )
    };
}

function getLatestRecordGroup(records) {
    const latestRecord = records.slice().sort((a, b) => {
        const aDate = parseTyphoonDate(a.report_datetime || a.updatedAt || a.forecast_time),
            bDate = parseTyphoonDate(b.report_datetime || b.updatedAt || b.forecast_time),
            aSerial = Number(a.serial || 0),
            bSerial = Number(b.serial || 0);

        if (aDate && bDate && aDate.getTime() !== bDate.getTime()) {
            return bDate - aDate;
        }
        return bSerial - aSerial;
    })[0];

    if (!latestRecord) {
        return [];
    }
    if (latestRecord.serial) {
        return records.filter(record => String(record.serial || '') === String(latestRecord.serial));
    }
    if (latestRecord.report_datetime) {
        return records.filter(record => record.report_datetime === latestRecord.report_datetime);
    }
    return records;
}

function normalizeTyphoonEventFromRecords(records, baseRecord = {}) {
    const latestRecords = getLatestRecordGroup(records),
        referenceRecord = latestRecords[0] || records[0] || baseRecord,
        detailSource = referenceRecord.detail && typeof referenceRecord.detail === 'object' ? referenceRecord.detail : {},
        normalizedPoints = latestRecords
            .map(normalizeTyphoonPoint)
            .filter(point => point.forecast_time || point.coordinate)
            .sort(sortTyphoonPoints),
        uniquePoints = Array.from(new Map(
            normalizedPoints.map(point => [`${point.forecast_time}|${point.isCurrent ? '1' : '0'}`, point])
        ).values()),
        current = uniquePoints.find(point => point.isCurrent) || uniquePoints[0] || null,
        forecasts = uniquePoints.filter(point => !point.isCurrent),
        displayNumber = getTyphoonDisplayNumber(
            getRecordValue(referenceRecord, ['display_number', 'displayNumber', 'typhoon_name_number', 'typhoonNameNumber'])
        ),
        detail = {
            matched_by: getRecordValue(detailSource, ['matched_by', 'matchedBy']),
            headline: getRecordValue(detailSource, ['headline']),
            comment: getRecordValue(detailSource, ['comment']),
            report_datetime: getRecordValue(detailSource, ['report_datetime', 'reportDateTime']),
            send_datetime: getRecordValue(detailSource, ['send_datetime', 'sendDateTime'])
        },
        typhoon = {
            eventid: toEventId(getRecordValue(referenceRecord, ['eventid', 'eventId']), referenceRecord.xmlid),
            serial: String(getRecordValue(referenceRecord, ['serial']) || ''),
            title: getRecordValue(referenceRecord, ['title']),
            headline: getRecordValue(referenceRecord, ['headline']),
            report_datetime: getRecordValue(referenceRecord, ['report_datetime', 'reportDateTime', 'updatedAt']),
            typhoon_name: getRecordValue(referenceRecord, ['typhoon_name', 'typhoonName']),
            typhoon_name_kana: getRecordValue(referenceRecord, ['typhoon_name_kana', 'typhoonNameKana']),
            typhoon_name_number: getRecordValue(referenceRecord, ['typhoon_name_number', 'typhoonNameNumber']),
            displayNumber,
            displayTitle: '',
            detail,
            current,
            forecasts,
            points: current ? [current, ...forecasts] : forecasts,
            isInDisplayBounds: toBoolean(getRecordValue(referenceRecord, ['isInDisplayBounds', 'is_in_display_bounds'])),
            isInitialDisplayTarget: toBoolean(getRecordValue(referenceRecord, ['isInitialDisplayTarget', 'is_initial_display_target'])),
            mapFeatureCollection: EMPTY_FEATURE_COLLECTION
        };

    if (current) {
        current.area_class ||= typhoon.area_class || getRecordValue(referenceRecord, ['area_class', 'areaClass']);
        current.intensity_class ||= typhoon.intensity_class || getRecordValue(referenceRecord, ['intensity_class', 'intensityClass']);
    }

    typhoon.displayTitle = getTyphoonDisplayTitle(typhoon);
    typhoon.mapFeatureCollection = current ?
        buildTyphoonFeatureCollection(current, forecasts) :
        EMPTY_FEATURE_COLLECTION;

    return typhoon;
}

function isAggregatedTyphoonEvent(record) {
    return !!(record && typeof record === 'object' && (
        Array.isArray(record.forecasts) ||
        Array.isArray(record.points) ||
        Array.isArray(record.records) ||
        record.current
    ));
}

function normalizeAggregatedTyphoonEvent(record) {
    const currentPoint = record.current ?
            normalizeTyphoonPoint({
                ...record,
                ...record.current,
                isCurrent: true,
                live_condition: record.current.live_condition ?? record.current.liveCondition ?? '1'
            }) :
            null,
        forecastSource = Array.isArray(record.forecasts) ? record.forecasts :
            Array.isArray(record.points) ? record.points :
            Array.isArray(record.records) ? record.records :
            [],
        normalizedForecasts = forecastSource
            .filter(point => !currentPoint || point.entityid !== currentPoint.entityid)
            .map(point => normalizeTyphoonPoint({
                ...record,
                ...point,
                isCurrent: false,
                live_condition: point.live_condition ?? point.liveCondition ?? '0'
            }))
            .filter(point => point.forecast_time || point.coordinate)
            .sort(sortTyphoonPoints),
        uniqueForecasts = Array.from(new Map(
            normalizedForecasts.map(point => [point.forecast_time || point.label || point.center_location || '', point])
        ).values()),
        current = currentPoint || uniqueForecasts[0] || null,
        forecasts = currentPoint ? uniqueForecasts : uniqueForecasts.slice(1),
        displayNumber = getTyphoonDisplayNumber(
            getRecordValue(record, ['display_number', 'displayNumber', 'typhoon_name_number', 'typhoonNameNumber'])
        ),
        detailSource = record.detail && typeof record.detail === 'object' ? record.detail : {},
        detail = {
            matched_by: getRecordValue(detailSource, ['matched_by', 'matchedBy']),
            headline: getRecordValue(detailSource, ['headline']),
            comment: getRecordValue(detailSource, ['comment']),
            report_datetime: getRecordValue(detailSource, ['report_datetime', 'reportDateTime']),
            send_datetime: getRecordValue(detailSource, ['send_datetime', 'sendDateTime'])
        },
        typhoon = {
            eventid: toEventId(getRecordValue(record, ['eventid', 'eventId']), record.xmlid),
            serial: String(getRecordValue(record, ['serial']) || ''),
            title: getRecordValue(record, ['title']),
            headline: getRecordValue(record, ['headline']),
            report_datetime: getRecordValue(record, ['report_datetime', 'reportDateTime', 'updatedAt']),
            typhoon_name: getRecordValue(record, ['typhoon_name', 'typhoonName']),
            typhoon_name_kana: getRecordValue(record, ['typhoon_name_kana', 'typhoonNameKana']),
            typhoon_name_number: getRecordValue(record, ['typhoon_name_number', 'typhoonNameNumber']),
            displayNumber,
            displayTitle: '',
            detail,
            current,
            forecasts,
            points: current ? [current, ...forecasts] : forecasts,
            isInDisplayBounds: toBoolean(getRecordValue(record, ['isInDisplayBounds', 'is_in_display_bounds'])),
            isInitialDisplayTarget: toBoolean(getRecordValue(record, ['isInitialDisplayTarget', 'is_initial_display_target'])),
            mapFeatureCollection: current ?
                buildTyphoonFeatureCollection(current, forecasts) :
                EMPTY_FEATURE_COLLECTION
        };

    if (current) {
        current.area_class ||= getRecordValue(record, ['area_class', 'areaClass']);
        current.intensity_class ||= getRecordValue(record, ['intensity_class', 'intensityClass']);
    }

    typhoon.displayTitle = getTyphoonDisplayTitle(typhoon);
    return typhoon;
}

function aggregateTyphoonAnalyzeItems(items) {
    const aggregatedTyphoons = [],
        groups = new Map();

    items.forEach(record => {
        const datatype = String(record.datatype || record.datatypename || '');

        if (datatype && datatype !== 'jmatyphoon_analyze') {
            return;
        }
        if (toBoolean(record.isCancelled)) {
            return;
        }

        if (isAggregatedTyphoonEvent(record)) {
            aggregatedTyphoons.push(normalizeAggregatedTyphoonEvent(record));
            return;
        }

        const eventid = toEventId(record.eventid, record.xmlid || record.report_datetime);

        if (!eventid) {
            return;
        }
        if (!groups.has(eventid)) {
            groups.set(eventid, []);
        }
        groups.get(eventid).push(record);
    });

    return [
        ...aggregatedTyphoons,
        ...Array.from(groups.values())
            .map(records => normalizeTyphoonEventFromRecords(records))
    ]
        .filter(typhoon => typhoon.eventid)
        .sort(sortTyphoons);
}

export function aggregateTyphoonRecords(payload) {
    return aggregateTyphoonAnalyzeItems(
        parseTyphoonPayload(payload).filter(record => record && typeof record === 'object')
    );
}

export async function loadTyphoons() {
    if (!configs.apiUrl.typhoon) {
        return [];
    }

    try {
        const response = await fetch(buildTyphoonRequestUrl(), {
            method: 'GET',
            cache: 'no-store',
            headers: buildApiRequestHeaders({
                'Content-Type': 'application/json'
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return aggregateTyphoonRecords(await response.json());
    } catch (error) {
        console.error('台風情報の取得に失敗しました:', error);
        return [];
    }
}

export function startTyphoonUpdates(callback, intervalMs = 60 * 1000) {
    loadTyphoons().then(callback);

    return setInterval(async () => {
        callback(await loadTyphoons());
    }, intervalMs);
}

export function stopTyphoonUpdates(intervalId) {
    if (intervalId) {
        clearInterval(intervalId);
    }
}
